// Loads the Laniqo website assistant (Brunon, pro persona) for signed-in users.
//
// The script tag is added only when NUXT_PUBLIC_ASSISTANT_URL is set for the
// instance; the chart sets it per instance values (website-assistant ADR-0010),
// so this code holds no vendor list. Logout leaves the app for Keycloak's
// end-session page, and that full reload removes Brunon — no teardown.

import {
  ASSISTANT_ANCHOR_SELECTOR,
  assistantWidgetSrc,
} from '~/utils/assistant'

export { assistantWidgetSrc }

export const ASSISTANT_SCRIPT_MARKER = 'data-laniqo-assistant'

type KeycloakLike = { authenticated?: boolean } | null | undefined

/**
 * Appends the assistant loader to document.head once, and only for an
 * authenticated user. Returns true when the loader is (already) present.
 */
export function injectAssistantWidget(
  assistantUrl: string,
  keycloak: KeycloakLike
): boolean {
  if (!assistantUrl?.trim() || !keycloak?.authenticated) {
    return false
  }

  const alreadyLoaded =
    'LaniqoAssistant' in window ||
    document.querySelector(`script[${ASSISTANT_SCRIPT_MARKER}]`) !== null
  if (alreadyLoaded) {
    return true
  }

  const script = document.createElement('script')
  script.src = assistantWidgetSrc(assistantUrl)
  script.async = true
  // Opens the chat after the first login in a desktop browser, never again
  // once closed, never on a phone (website-assistant ADR-0013)
  script.dataset.open = 'desktop-once'
  script.dataset.anchor = ASSISTANT_ANCHOR_SELECTOR
  script.setAttribute(ASSISTANT_SCRIPT_MARKER, '')
  document.head.appendChild(script)
  return true
}

export default defineNuxtPlugin((nuxtApp) => {
  const assistantUrl = String(useRuntimeConfig().public.assistantUrl || '')
  if (!assistantUrl.trim()) {
    return
  }

  // $keycloak is read lazily: this plugin may run before keycloak.js, and the
  // initial navigation (where auth.global.js decides) may finish before or
  // after this plugin registers. app:mounted covers the first page; afterEach
  // covers any later successful navigation once the user is authenticated.
  const tryInject = () => {
    injectAssistantWidget(assistantUrl, nuxtApp.$keycloak as KeycloakLike)
  }

  nuxtApp.hook('app:mounted', tryInject)
  useRouter().afterEach((_to, _from, failure) => {
    if (!failure) {
      tryInject()
    }
  })
})
